import { useCheckFieldAvailabilityQuery } from "@/shared/api/fieldValidation";
import type { FieldAvailabilityResponse } from "@/shared/api/fieldValidation";
import { useDebouncedValue } from "./useDebouncedValue";

export type FieldAvailabilityState = "idle" | "checking" | "available" | "taken" | "error";

interface Params {
  resource: string;
  field: string;
  value: string;
  isFormatValid: boolean;
  enabled?: boolean;
  exclude_id?: number;
  exclude_token?: string;
}

interface Result {
  state: FieldAvailabilityState;
  data: FieldAvailabilityResponse | undefined;
}

export function useFieldAvailability({
  resource,
  field,
  value,
  isFormatValid,
  enabled = true,
  exclude_id,
  exclude_token,
}: Params): Result {
  const debounced = useDebouncedValue(value, 400);
  const shouldCheck = enabled && isFormatValid && debounced.trim().length > 0;

  const { data, isFetching, isSuccess, isError } = useCheckFieldAvailabilityQuery(
    { resource, field, value: debounced, exclude_id, exclude_token },
    { skip: !shouldCheck },
  );

  const state = deriveState({
    enabled,
    isFormatValid,
    value,
    debounced,
    isFetching,
    isSuccess,
    isError,
    data,
  });

  return { state, data: state === "taken" || state === "available" ? data : undefined };
}

interface DeriveParams {
  enabled: boolean;
  isFormatValid: boolean;
  value: string;
  debounced: string;
  isFetching: boolean;
  isSuccess: boolean;
  isError: boolean;
  data: FieldAvailabilityResponse | undefined;
}

function deriveState({
  enabled,
  isFormatValid,
  value,
  debounced,
  isFetching,
  isSuccess,
  isError,
  data,
}: DeriveParams): FieldAvailabilityState {
  if (!enabled || !isFormatValid || value.trim().length === 0) return "idle";
  if (value !== debounced || isFetching) return "checking";
  if (isError) return "error";
  if (isSuccess && data) return data.available ? "available" : "taken";
  return "checking";
}
